import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Badge } from '../../components/ui/Badge';
import { 
  Shield, 
  Save, 
  RefreshCw, 
  Plus, 
  Edit, 
  Trash2,
  Filter,
  Search,
  Download,
  Upload,
  X,
  Lock,
  Unlock,
  FileText,
  Sparkles
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { apiRequest } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorHandler';
import toast from 'react-hot-toast';
import StatusFilterDropdown from '../../components/filters/StatusFilterDropdown';
import { useDynamicMenu } from '../../hooks/useDynamicMenu';
import { ChevronRight, ChevronDown, CheckSquare, Square } from 'lucide-react';
import authService from '../../api/authService';
import { Switch } from '../../components/ui/Switch';
import { FEATURES_LIST_UPDATED_EVENT } from '../../components/globalfeatures';

const RoleManagement = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    isActive: true,
    isRequesterRole: false,
  });
  const [refreshing, setRefreshing] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Permission management modal state
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedRoleForPermission, setSelectedRoleForPermission] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [screenPermissions, setScreenPermissions] = useState({}); // { screenId: { canView, canInsert, canUpdate, canDelete } }
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [permissionsApiModules, setPermissionsApiModules] = useState({}); // Store modules from permissions API response
  
  // Table pagination and search state for permission modal
  const [permissionSearchQuery, setPermissionSearchQuery] = useState('');
  const [permissionPageSize, setPermissionPageSize] = useState(10);
  const [permissionCurrentPage, setPermissionCurrentPage] = useState(0);

  const [showMasterFeatureModal, setShowMasterFeatureModal] = useState(false);
  const [selectedRoleForMasterFeatures, setSelectedRoleForMasterFeatures] = useState(null);
  const [masterFeaturesCatalog, setMasterFeaturesCatalog] = useState([]);
  const [roleFeaturePermissions, setRoleFeaturePermissions] = useState({});
  const [loadingMasterFeatures, setLoadingMasterFeatures] = useState(false);
  const [savingMasterFeatures, setSavingMasterFeatures] = useState(false);
  const [masterFeatureSearchQuery, setMasterFeatureSearchQuery] = useState('');
  
  // Use dynamic menu hook to get modules and screens
  const { menuData, loading: menuLoading } = useDynamicMenu();

  useEffect(() => {
    loadRoleData();
  }, []);

  const loadRoleData = async () => {
    setLoading(true);
    try {
      const response = await apiRequest('/gateway/registration/api/signup/organization/roles', {
        method: 'GET'
      });
      
      // Handle different response structures
      let rolesData = [];
      if (Array.isArray(response)) {
        rolesData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        rolesData = response.data;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        rolesData = response.data.data;
      }
      
      // Map backend data to frontend format
      const mappedRoles = rolesData.map((role, index) => ({
        id: role.roleId || role.role_id || role.id || `role_${index}`,
        name: role.roleName || role.role_name || role.name || '',
        description: role.description || '',
        roleCode: role.roleCode || role.role_code || '',
        isActive: role.isActive !== undefined ? role.isActive : (role.is_active !== undefined ? role.is_active : true),
        isRequesterRole: role.isRequesterRole === true || role.is_requester_role === true,
      }));
      
      setAllRoles(mappedRoles);
      setRoles(mappedRoles);
    } catch (error) {
      console.error('Error loading role data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load role data. Please refresh the page.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const escapeCsvCell = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleExportCsv = () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      if (roles.length === 0) {
        toast.error('No roles to export');
        return;
      }
      const headers = ['S.No', 'Role Name', 'Description', 'Active Status'];
      const rows = roles.map((r, i) => [
        i + 1,
        r.name || '',
        r.description || '',
        r.isActive ? 'Active' : 'Inactive'
      ].map(escapeCsvCell));
      const csv = [headers.map(escapeCsvCell).join(','), ...rows.map((r) => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `roles-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Roles exported as CSV');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      if (roles.length === 0) {
        toast.error('No roles to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'portrait' });
      doc.setFontSize(14);
      doc.text('Role Management', 14, 15);
      doc.setFontSize(10);
      const body = roles.map((r, i) => [
        i + 1,
        (r.name || '').substring(0, 30),
        (r.description || '').substring(0, 50),
        r.isActive ? 'Active' : 'Inactive'
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['S.No', 'Role Name', 'Description', 'Active Status']],
        body,
        styles: { fontSize: 8 }
      });
      doc.save(`roles-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Roles exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleCreateRole = () => {
    setNewRole({
      name: '',
      description: '',
      isActive: true,
      isRequesterRole: false,
    });
    setEditingRole(null);
    setShowRoleForm(true);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setNewRole({
      name: role.name || '',
      description: role.description || '',
      roleCode: role.roleCode || '',
      isActive: role.isActive !== undefined ? role.isActive : true,
      isRequesterRole: role.isRequesterRole === true,
    });
    setShowRoleForm(true);
  };

  const handleSaveRole = async () => {
    try {
      setSaving(true);
      
      const roleData = {
        roleName: newRole.name,
        description: newRole.description,
        isActive: newRole.isActive,
        isRequesterRole: newRole.isRequesterRole === true,
      };
      
      // Include roleCode for updates (required by backend DTO)
      if (editingRole && editingRole.roleCode) {
        roleData.roleCode = editingRole.roleCode;
      } else if (newRole.roleCode) {
        roleData.roleCode = newRole.roleCode;
      } else if (!editingRole) {
        // For new roles, default roleCode to roleName (backend will also handle this, but sending it explicitly)
        // Generate roleCode from roleName: remove special chars, replace spaces with underscores, uppercase
        if (newRole.name && newRole.name.trim()) {
          roleData.roleCode = newRole.name
            .trim()
            .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .toUpperCase()
            .substring(0, 50); // Limit to 50 characters
        }
      }
      
      if (editingRole) {
        // Update existing role
        const response = await apiRequest(`/gateway/registration/api/signup/organization/roles/${editingRole.id}`, {
          method: 'PUT',
          body: roleData
        });
        
        if (response) {
          await loadRoleData(); // Reload roles after update
          toast.success('Role updated successfully');
        } else {
          throw new Error('Failed to update role');
        }
      } else {
        // Create new role
        const response = await apiRequest('/gateway/registration/api/signup/organization/roles', {
          method: 'POST',
          body: roleData
        });
        
        if (response) {
          await loadRoleData(); // Reload roles after create
          toast.success('Role created successfully');
        } else {
          throw new Error('Failed to create role');
        }
      }
      
      setShowRoleForm(false);
      setEditingRole(null);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while saving the role';
      toast.error(errorMessage);
      console.error('Error saving role:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCloseRoleForm = () => {
    setShowRoleForm(false);
    setEditingRole(null);
    setNewRole({
      name: '',
      description: '',
      isActive: true,
      isRequesterRole: false,
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadRoleData();
      toast.success('Role data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh role data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) {
      return;
    }
    
    try {
      const response = await apiRequest(`/gateway/registration/api/signup/organization/roles/${roleId}`, {
        method: 'DELETE'
      });
      
      if (response) {
        await loadRoleData(); // Reload roles after delete
        toast.success('Role deleted successfully');
      } else {
        throw new Error('Failed to delete role');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete role';
      toast.error(errorMessage);
    }
  };

  const handleToggleLock = async (role) => {
    // Open permission management modal instead of toggling lock
    setSelectedRoleForPermission(role);
    setShowPermissionModal(true);
    setScreenPermissions({}); // Reset permissions
    setExpandedModules({}); // Reset expanded modules
    
    // Get tenant ID from sessionStorage, localStorage, or currentUser, default to 1
    const currentUser = authService.getCurrentUser();
    const tenantId = parseInt(
      sessionStorage.getItem('tenantId') || 
      currentUser?.tenantId || 
      '1', 
      10
    );
    
    // Load existing permissions for this role using the screens-permission-by-roleid API
    try {
      const response = await apiRequest(`/gateway/registration/userrolescreenpermission/screens-permission-by-roleid/${role.id}/${tenantId}`, {
        method: 'GET'
      });
      
      //console.log('📦 Raw screens-permission-by-roleid API response:', response);
      //console.log('📦 Response type:', typeof response);
      //console.log('📦 Response.data:', response?.data);
      //console.log('📦 Response.data type:', typeof response?.data);
      
      // Handle different response structures
      // Latest API response structure: { "data": { "module_label": [screens...] } }
      // The data is a Map/Object where key is module_label and value is array of screens
      let permissionsDataMap = {};
      
      // Priority 1: Check for response.data (most common structure)
      if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        // ResponseBean.data is a Map<String, List<RoleScreensListDTO>>
        permissionsDataMap = response.data;
        //console.log('✅ Using response.data');
      } 
      // Priority 2: Check for response.response
      else if (response?.response && typeof response.response === 'object' && !Array.isArray(response.response)) {
        permissionsDataMap = response.response;
        //console.log('✅ Using response.response');
      } 
      // Priority 3: Check if response itself is the data object
      else if (typeof response === 'object' && !Array.isArray(response) && response !== null) {
        // Check if it looks like the data structure (has module labels as keys)
        const keys = Object.keys(response);
        const hasModuleLikeKeys = keys.some(key => 
          typeof response[key] === 'object' && 
          Array.isArray(response[key]) &&
          response[key].length > 0 &&
          response[key][0]?.module_label
        );
        
        if (hasModuleLikeKeys) {
          permissionsDataMap = response;
          //console.log('✅ Using direct response object (has module-like structure)');
        } else {
          console.warn('⚠️ Response structure not recognized, trying as-is');
          permissionsDataMap = response;
        }
      } else {
        console.error('❌ Unable to parse response structure:', response);
        toast.error('Invalid response structure from API');
        return;
      }
      
      //console.log('📋 Extracted permissions data (Map by module_label):', permissionsDataMap);
      //console.log('📋 Module labels found:', Object.keys(permissionsDataMap));
      //console.log('📋 Current menuData:', menuData);
      //console.log('📋 MenuData length:', menuData?.length || 0);
      
      // Store the permissions API modules for use in the modal
      setPermissionsApiModules(permissionsDataMap);
      
      // Map the API response to screenPermissions state
      // The API returns a Map<String, List<RoleScreensListDTO>> where:
      // - Key: module_label (e.g., "Dashboard", "Users")
      // - Value: Array of screens with permission flags
      // Each screen has: screen_id, screen_name, screen_label, can_view, can_insert, can_update, can_delete, etc.
      const existingPermissions = {};
      const screenIdMapping = {}; // Map to help match menuData screens with permissions
      
      //console.log('📦 Processing permissions data from screens-permission-by-roleid API:', permissionsDataMap);
      
      // Iterate over the Map (module_label -> screens array)
      // Response structure: { "Integrated Module": [...], "Administration": [...] }
      Object.entries(permissionsDataMap).forEach(([moduleLabel, screens]) => {
        const screenList = Array.isArray(screens) ? screens : [];
        
        if (screenList.length === 0) {
          //console.log(`📁 Module "${moduleLabel}": No screens found`);
          return;
        }
        
        //console.log(`📁 Processing module: "${moduleLabel}", screens: ${screenList.length}`);
        //console.log(`📁 First screen in module:`, screenList[0]);
        
        screenList.forEach((screen, screenIndex) => {
          // The screens-permission-by-roleid API returns RoleScreensListDTO with screen_id
          // Fields: can_view, can_insert, can_update, can_delete, screen_id, screen_name, screen_label, routing, module_label
          const screenIdFromAPI = screen.screen_id;
          const screenRouting = screen.routing;
          const screenName = screen.screen_name || screen.screen_label;
          
          //console.log(`  📄 Screen ${screenIndex + 1}:`, {
          //   screen_id: screenIdFromAPI,
          //   screen_name: screenName,
          //   routing: screenRouting,
          //   can_view: screen.can_view,
          //   can_insert: screen.can_insert,
          //   can_update: screen.can_update,
          //   can_delete: screen.can_delete
          // });
          
          if (!screenIdFromAPI) {
            console.warn(`  ⚠️ Screen missing screen_id:`, screen);
            return;
          }
          
          // Find matching screen in menuData by ID first, then by routing/name
          let matchingScreenId = screenIdFromAPI; // Default to API's screen_id
          
          // Only try to match with menuData if it's available
          if (menuData && menuData.length > 0) {
            // First, try to find by screen_id matching menuData's id
            // Convert both to numbers for comparison
            const apiScreenIdNum = typeof screenIdFromAPI === 'string' ? parseInt(screenIdFromAPI, 10) : screenIdFromAPI;
            const menuScreenById = menuData
              .flatMap(m => (m.submenu || []).map(s => ({ ...s, moduleName: m.menumoduleName })))
              .find(s => {
                const menuScreenIdNum = typeof s.id === 'string' ? parseInt(s.id, 10) : s.id;
                return menuScreenIdNum === apiScreenIdNum || s.id === screenIdFromAPI;
              });
            
            if (menuScreenById) {
              matchingScreenId = menuScreenById.id;
              //console.log(`  ✅ Matched by ID: API screen_id ${screenIdFromAPI} (${typeof screenIdFromAPI}) -> menuData id ${matchingScreenId} (${typeof matchingScreenId})`);
            } else {
              // Debug: log available menuData screen IDs for this module
              const moduleScreens = menuData
                .flatMap(m => (m.submenu || []).map(s => ({ id: s.id, name: s.screenName || s.screenLable, routing: s.routing })));
              //console.log(`  🔍 Available menuData screen IDs:`, moduleScreens.map(s => s.id));
              //console.log(`  🔍 Looking for screen_id: ${screenIdFromAPI} (${typeof screenIdFromAPI})`);
              // Fallback: try to match by routing or screen name
              const menuScreenByRoute = menuData
                .flatMap(m => (m.submenu || []).map(s => ({ ...s, moduleName: m.menumoduleName })))
                .find(s => 
                  (screenRouting && s.routing && (s.routing === screenRouting || s.routing.includes(screenRouting))) ||
                  (screenName && (s.screenName === screenName || s.screenLable === screenName))
                );
              
              if (menuScreenByRoute) {
                matchingScreenId = menuScreenByRoute.id;
                //console.log(`  ✅ Matched by routing/name: API screen_id ${screenIdFromAPI} -> menuData id ${matchingScreenId} (${menuScreenByRoute.screenName || menuScreenByRoute.screenLable})`);
              } else {
                // Use screen_id from API as fallback
                matchingScreenId = screenIdFromAPI;
                //console.log(`  ℹ️ No menuData match for screen_id ${screenIdFromAPI}, using API screen_id as key`);
              }
            }
          } else {
            //console.log(`  ℹ️ MenuData not available, using API screen_id ${screenIdFromAPI} as key`);
          }
          
          // Map snake_case API keys to camelCase state
          // RoleScreensListDTO has: can_view, can_insert, can_update, can_delete (all boolean)
          // Convert to boolean explicitly (they should already be boolean, but ensure it)
          const permissionObj = {
            canView: Boolean(screen.can_view === true || screen.can_view === 'true' || screen.can_view === 1),
            canInsert: Boolean(screen.can_insert === true || screen.can_insert === 'true' || screen.can_insert === 1),
            canUpdate: Boolean(screen.can_update === true || screen.can_update === 'true' || screen.can_update === 1),
            canDelete: Boolean(screen.can_delete === true || screen.can_delete === 'true' || screen.can_delete === 1)
          };
          
          existingPermissions[matchingScreenId] = permissionObj;
          screenIdMapping[screenIdFromAPI] = matchingScreenId;
          
          //console.log(`  ✅ Mapped permissions: API screen_id ${screenIdFromAPI} -> key ${matchingScreenId}:`, permissionObj);
        });
      });
      
      //console.log('✅ Final mapped permissions (keyed by menuData screen.id):', existingPermissions);
      //console.log('📊 Total screens with permissions:', Object.keys(existingPermissions).length);
      //console.log('🔑 Screen IDs in permissions:', Object.keys(existingPermissions));
      //console.log('🗺️ Screen ID mapping (API screen_id -> menuData id):', screenIdMapping);
      setScreenPermissions(existingPermissions);
    } catch (error) {
      console.error('❌ Error loading existing permissions:', error);
      toast.error('Failed to load existing permissions. You can still set new ones.');
      // Continue without existing permissions - user can set new ones
    }
  };
  
  const toggleModuleExpanded = (moduleName) => {
    setExpandedModules(prev => {
      const isCurrentlyExpanded = prev[moduleName];
      
      // If clicking on an already expanded module, close it
      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [moduleName]: false
        };
      }
      
      // If opening a new module, close all others and open this one
      return {
        [moduleName]: true
      };
    });
    
    // Reset pagination when expanding a module
    setPermissionCurrentPage(0);
  };
  
  const toggleScreenPermission = (screenId, permissionType) => {
    setScreenPermissions(prev => {
      const current = prev[screenId] || {
        canView: false,
        canInsert: false,
        canUpdate: false,
        canDelete: false
      };
      return {
        ...prev,
        [screenId]: {
          ...current,
          [permissionType]: !current[permissionType]
        }
      };
    });
  };
  
  const handleSavePermissions = async () => {
    if (!selectedRoleForPermission) return;
    const tenantId = parseInt(
      sessionStorage.getItem('tenantId'));
    try {
      setSavingPermissions(true);
      
      // Prepare payload - array of permission objects
      // Send all screens that have been interacted with (even if all permissions are false)
      const permissionsPayload = Object.entries(screenPermissions).map(([screenId, permissions]) => ({
        role_id: selectedRoleForPermission.id,
        screen_id: parseInt(screenId),
        can_view: permissions.canView || false,
        can_insert: permissions.canInsert || false,
        can_update: permissions.canUpdate || false,
        can_delete: permissions.canDelete || false
      }));
      
      if (permissionsPayload.length === 0) {
        toast.error('Please configure permissions for at least one screen');
        return;
      }
      
      // Call the API (tenantId removed - roles are now master/global)
      const response = await apiRequest(`/gateway/registration/userrolescreenpermission/save/${tenantId}`, {
        method: 'POST',
        body: permissionsPayload
      });
      
      if (response) {
        toast.success('Permissions saved successfully');
        setShowPermissionModal(false);
        setSelectedRoleForPermission(null);
        setScreenPermissions({});
      } else {
        throw new Error('Failed to save permissions');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save permissions';
      toast.error(errorMessage);
      console.error('Error saving permissions:', error);
    } finally {
      setSavingPermissions(false);
    }
  };
  
  const handleClosePermissionModal = () => {
    setShowPermissionModal(false);
    setSelectedRoleForPermission(null);
    setScreenPermissions({});
    setExpandedModules({});
    setPermissionsApiModules({});
    setPermissionSearchQuery('');
    setPermissionCurrentPage(0);
  };

  const getTenantIdForRoleApis = () => {
    const currentUser = authService.getCurrentUser();
    return parseInt(
      sessionStorage.getItem('tenantId') || currentUser?.tenantId || '1',
      10
    );
  };

  const parseRegistrationListResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.response)) return response.response;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    return [];
  };

  const handleOpenMasterFeaturesModal = async (role) => {
    setSelectedRoleForMasterFeatures(role);
    setShowMasterFeatureModal(true);
    setMasterFeatureSearchQuery('');
    setRoleFeaturePermissions({});
    setMasterFeaturesCatalog([]);
    setLoadingMasterFeatures(true);

    const tenantId = getTenantIdForRoleApis();
    const roleId = parseInt(role.id, 10);

    try {
      const [allRes, byRoleRes] = await Promise.all([
        apiRequest('/gateway/api/ticket/master-features/all', { method: 'GET' }),
        apiRequest(
          `/gateway/registration/userrolefeaturepermission/by-role/${roleId}/${tenantId}`,
          { method: 'GET' }
        ).catch(() => null)
      ]);

      let catalog = [];
      if (Array.isArray(allRes)) catalog = allRes;
      else if (Array.isArray(allRes?.data)) catalog = allRes.data;
      else if (Array.isArray(allRes?.data?.data)) catalog = allRes.data.data;

      setMasterFeaturesCatalog(catalog);

      const assigned = parseRegistrationListResponse(byRoleRes);
      const permMap = {};
      assigned.forEach((row) => {
        const fid = row.feature_id ?? row.featureId ?? row.FEATURE_ID;
        if (fid == null) return;
        const idKey = String(fid);
        permMap[idKey] = {
          canView: Boolean(
            row.can_view === true || row.can_view === 1 || row.can_view === 'true'
          ),
          canInsert: Boolean(
            row.can_insert === true || row.can_insert === 1 || row.can_insert === 'true'
          ),
          canUpdate: Boolean(
            row.can_update === true || row.can_update === 1 || row.can_update === 'true'
          ),
          canDelete: Boolean(
            row.can_delete === true || row.can_delete === 1 || row.can_delete === 'true'
          )
        };
      });

      catalog.forEach((f) => {
        const id = f.id ?? f.feature_id;
        if (id == null) return;
        const idKey = String(id);
        if (!permMap[idKey]) {
          permMap[idKey] = {
            canView: false,
            canInsert: false,
            canUpdate: false,
            canDelete: false
          };
        }
      });

      setRoleFeaturePermissions(permMap);
    } catch (e) {
      console.error('Error loading master features for role:', e);
      toast.error(extractErrorMessage(e, 'Failed to load master features'));
    } finally {
      setLoadingMasterFeatures(false);
    }
  };

  const toggleRoleMasterFeature = (featureId, enabled) => {
    const key = String(featureId);
    setRoleFeaturePermissions((prev) => {
      const current = prev[key] || {
        canView: false,
        canInsert: false,
        canUpdate: false,
        canDelete: false
      };
      return {
        ...prev,
        [key]: {
          ...current,
          canView: enabled
        }
      };
    });
  };

  const toggleRoleMasterFeatureUpdate = (featureId, enabled) => {
    const key = String(featureId);
    setRoleFeaturePermissions((prev) => {
      const current = prev[key] || {
        canView: false,
        canInsert: false,
        canUpdate: false,
        canDelete: false
      };
      return {
        ...prev,
        [key]: {
          ...current,
          canUpdate: enabled
        }
      };
    });
  };

  const handleSaveMasterFeatures = async () => {
    if (!selectedRoleForMasterFeatures) return;
    const tenantId = getTenantIdForRoleApis();
    const roleId = parseInt(selectedRoleForMasterFeatures.id, 10);

    const payload = masterFeaturesCatalog
      .map((f) => {
        const fid = f.id ?? f.feature_id;
        if (fid == null) return null;
        const key = String(fid);
        const p = roleFeaturePermissions[key] || {
          canView: false,
          canInsert: false,
          canUpdate: false,
          canDelete: false
        };
        return {
          role_id: roleId,
          feature_id: parseInt(fid, 10),
          can_view: p.canView,
          can_insert: p.canInsert,
          can_update: p.canUpdate,
          can_delete: p.canDelete
        };
      })
      .filter(Boolean);

    if (payload.length === 0) {
      toast.error('No master features to save');
      return;
    }

    try {
      setSavingMasterFeatures(true);
      const response = await apiRequest(
        `/gateway/registration/userrolefeaturepermission/save/${tenantId}`,
        {
          method: 'POST',
          body: payload
        }
      );
      if (response) {
        const savedRoleId = selectedRoleForMasterFeatures.id;
        const me = authService.getCurrentUser();
        if (me?.roleId != null && String(me.roleId) === String(savedRoleId)) {
          try {
            const res = await apiRequest(
              `/gateway/registration/userrolefeaturepermission/by-role/${parseInt(me.roleId, 10)}/${tenantId}`,
              { method: 'GET' }
            );
            const list = parseRegistrationListResponse(res);
            sessionStorage.setItem('featuresList', JSON.stringify(list));
          } catch (_) {
            /* non-fatal */
          }
        }
        toast.success('Master feature access saved');
        setShowMasterFeatureModal(false);
        setSelectedRoleForMasterFeatures(null);
        setMasterFeaturesCatalog([]);
        setRoleFeaturePermissions({});
        window.dispatchEvent(new CustomEvent(FEATURES_LIST_UPDATED_EVENT));
      } else {
        throw new Error('Save failed');
      }
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to save master features'));
    } finally {
      setSavingMasterFeatures(false);
    }
  };

  const handleCloseMasterFeatureModal = () => {
    setShowMasterFeatureModal(false);
    setSelectedRoleForMasterFeatures(null);
    setMasterFeaturesCatalog([]);
    setRoleFeaturePermissions({});
    setMasterFeatureSearchQuery('');
  };
  
  // Get screens for currently expanded module with search and pagination
  const getFilteredAndPaginatedScreens = (screens) => {
    // screens is an array from the permissions API response
    if (!screens || !Array.isArray(screens)) return { allScreens: [], paginatedScreens: [], totalCount: 0 };
    
    let filteredScreens = [...screens];
    
    // Apply search filter
    if (permissionSearchQuery.trim()) {
      const query = permissionSearchQuery.toLowerCase();
      filteredScreens = filteredScreens.filter(screen => 
        (screen.screen_label || screen.screen_name || '').toLowerCase().includes(query) ||
        (screen.routing || '').toLowerCase().includes(query)
      );
    }
    
    // Apply pagination
    const startIndex = permissionCurrentPage * permissionPageSize;
    const endIndex = startIndex + permissionPageSize;
    const paginatedScreens = filteredScreens.slice(startIndex, endIndex);
    
    return { allScreens: filteredScreens, paginatedScreens, totalCount: filteredScreens.length };
  };




  // Filter roles based on selected status and search query
  useEffect(() => {
    let filteredRoles = [...allRoles];

    // Filter by status (active/inactive)
    if (selectedStatus) {
      if (selectedStatus.toLowerCase() === 'active') {
        filteredRoles = filteredRoles.filter(role => role.isActive === true);
      } else if (selectedStatus.toLowerCase() === 'inactive') {
        filteredRoles = filteredRoles.filter(role => role.isActive === false);
      }
    }

    // Filter by search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filteredRoles = filteredRoles.filter(role =>
        role.name?.toLowerCase().includes(query) ||
        role.description?.toLowerCase().includes(query)
      );
    }

    setRoles(filteredRoles);
  }, [selectedStatus, searchQuery, allRoles]);


  const roleStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Role Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Manage roles and permissions</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="flex items-center gap-2 border-[#006600] text-[#006600] hover:bg-green-50 disabled:border-gray-300 disabled:text-gray-400"
          >
            <RefreshCw className={`h-4 w-4 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            onClick={handleCreateRole}
            className="flex items-center gap-2 bg-[#006600] hover:bg-[#005500] text-white border-[#006600]"
          >
            <Plus className="h-4 w-4" />
            Create Role
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {(
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Roles List
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Search roles..."
                        className="pl-10 w-64"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <StatusFilterDropdown
                      value={selectedStatus}
                      options={roleStatusOptions}
                      placeholder="Filter by Status"
                      onChange={setSelectedStatus}
                      icon={Filter}
                    />
                  </div>
                  <div className="flex items-center gap-2 relative">
                    <div className="relative flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-[#006600] text-[#006600] hover:bg-green-50 flex items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExportMenuOpen((v) => !v);
                        }}
                        disabled={loading || exporting}
                      >
                        <Download className="h-4 w-4" />
                        {exporting ? 'Exporting…' : 'Export'}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {exportMenuOpen && (
                        <>
                          <div className="fixed inset-0 z-40" aria-hidden onClick={() => setExportMenuOpen(false)} />
                          <div className="absolute right-0 top-full mt-1 py-1 z-50 min-w-[140px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                              onClick={handleExportCsv}
                            >
                              <FileText className="h-4 w-4" />
                              Export CSV
                            </button>
                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                              onClick={handleExportPdf}
                            >
                              <FileText className="h-4 w-4" />
                              Export PDF
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {roles.length === 0 && (
                  <div className="text-center py-12">
                    <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">No roles found</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedStatus || searchQuery.trim() !== ''
                        ? 'Try adjusting your filters'
                        : 'No roles available'}
                    </p>
                    {(selectedStatus || searchQuery.trim() !== '') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedStatus(null);
                          setSearchQuery('');
                        }}
                        className="mt-4 border-[#006600] text-[#006600] hover:bg-green-50"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">S.No</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Active Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                            No roles found. Click "Create Role" to add a new role.
                          </td>
                        </tr>
                      ) : (
                        roles.map((role, index) => (
                          <tr key={role.id} className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {index + 1}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {role.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {role.description || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <Badge 
                                variant="outline" 
                                className={role.isActive 
                                  ? "bg-green-50 text-green-700 border-green-200" 
                                  : "bg-red-50 text-red-700 border-red-200"
                                }
                              >
                                {role.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditRole(role)}
                                  className="border-[#006600] text-[#006600] hover:bg-green-50"
                                  title="Edit Role"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleLock(role)}
                                  className={role.isActive 
                                    ? "border-orange-500 text-orange-600 hover:bg-orange-50" 
                                    : "border-green-500 text-green-600 hover:bg-green-50"
                                  }
                                  title={role.isActive ? "Lock Role" : "Unlock Role"}
                                >
                                  {role.isActive ? (
                                    <Lock className="h-4 w-4" />
                                  ) : (
                                    <Unlock className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenMasterFeaturesModal(role)}
                                  className="border-indigo-500 text-indigo-600 hover:bg-indigo-50"
                                  title="Master features"
                                >
                                  <Sparkles className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Create/Edit Role Modal */}
      {showRoleForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b border-gray-200">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  {editingRole ? 'Edit Role' : 'Create New Role'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseRoleForm}
                  className="h-8 w-8 p-0 text-[#006600] hover:bg-green-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <div className="space-y-2">
                <Label htmlFor="role-name">Role Name *</Label>
                <Input
                  id="role-name"
                  placeholder="e.g., Senior Agent"
                  value={newRole.name}
                  onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role-description">Description *</Label>
                <textarea
                  id="role-description"
                  placeholder="Describe the role's purpose and responsibilities..."
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                  rows="4"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <Label htmlFor="role-active" className="cursor-pointer">
                  Active Status
                </Label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="role-active"
                    checked={newRole.isActive}
                    onChange={(e) => setNewRole({ ...newRole, isActive: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div>
                  <Label htmlFor="role-requester" className="cursor-pointer">
                    Requester Role
                  </Label>
                  <p className="text-xs text-gray-500 mt-1">
                    Users with this role can raise and own tickets (e.g. Customer, Staff, Student).
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="role-requester"
                    checked={newRole.isRequesterRole === true}
                    onChange={(e) => setNewRole({ ...newRole, isRequesterRole: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={handleCloseRoleForm}
                  disabled={saving}
                  className="border-[#006600] text-[#006600] hover:bg-green-50 disabled:border-gray-300 disabled:text-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveRole}
                  disabled={saving || !newRole.name.trim() || !newRole.description.trim()}
                  className="bg-[#006600] hover:bg-[#005500] text-white border-[#006600] disabled:bg-gray-400 disabled:border-gray-400"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {editingRole ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingRole ? 'Update Role' : 'Create Role'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Permission Management Modal */}
      {showPermissionModal && selectedRoleForPermission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader className="border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Manage Permissions - {selectedRoleForPermission.name}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClosePermissionModal}
                  className="h-8 w-8 p-0 text-[#006600] hover:bg-green-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto pt-6">
              {menuLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-6 w-6 animate-spin text-[#006600]" />
                  <span className="ml-2 text-gray-600">Loading modules...</span>
                </div>
              ) : menuData.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No modules available</p>
                </div>
              ) : Object.keys(permissionsApiModules).length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No permissions data available. Click the lock button to load permissions.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Display modules from permissions API response */}
                  {Object.entries(permissionsApiModules).map(([moduleLabel, screens]) => {
                    const isExpanded = expandedModules[moduleLabel];
                    const screenList = Array.isArray(screens) ? screens : [];
                      
                    return (
                      <div key={moduleLabel} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleModuleExpanded(moduleLabel)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-t-lg transition-colors"
                        >
                          <span className="font-semibold text-gray-900">{moduleLabel}</span>
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-gray-600" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-600" />
                          )}
                        </button>
                        
                        {isExpanded && (
                          <div className="p-4">
                            {/* Search and Show entries controls */}
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-700">Show</span>
                                <select
                                  value={permissionPageSize}
                                  onChange={(e) => {
                                    setPermissionPageSize(Number(e.target.value));
                                    setPermissionCurrentPage(0);
                                  }}
                                  className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#006600]"
                                >
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                  <option value={100}>100</option>
                                </select>
                                <span className="text-sm text-gray-700">entries</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-700">Search:</span>
                                <Input
                                  type="text"
                                  placeholder=""
                                  value={permissionSearchQuery}
                                  onChange={(e) => {
                                    setPermissionSearchQuery(e.target.value);
                                    setPermissionCurrentPage(0);
                                  }}
                                  className="w-48"
                                />
                              </div>
                            </div>
                            
                            {/* Table */}
                            {(() => {
                              const { allScreens, paginatedScreens, totalCount } = getFilteredAndPaginatedScreens(screenList);
                                
                                if (allScreens.length === 0) {
                                  return (
                                    <p className="text-sm text-gray-500 text-center py-4">
                                      {permissionSearchQuery ? 'No screens found matching your search' : 'No active screens in this module'}
                                    </p>
                                  );
                                }
                                
                                const totalPages = Math.ceil(totalCount / permissionPageSize);
                                const startIndex = permissionCurrentPage * permissionPageSize;
                                const endIndex = Math.min(startIndex + permissionPageSize, totalCount);
                                
                                return (
                                  <>
                                    <div className="overflow-x-auto">
                                      <table className="w-full border-collapse">
                                        <thead>
                                          <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-r border-gray-200">
                                              <div className="flex items-center gap-1">
                                                S No
                                                <div className="flex flex-col">
                                                  <ChevronRight className="h-3 w-3 rotate-[-90deg] opacity-50" />
                                                  <ChevronRight className="h-3 w-3 rotate-90 opacity-50" />
                                                </div>
                                              </div>
                                            </th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-r border-gray-200">
                                              <div className="flex items-center gap-1">
                                                Screen Name
                                                <div className="flex flex-col">
                                                  <ChevronRight className="h-3 w-3 rotate-[-90deg] opacity-50" />
                                                  <ChevronRight className="h-3 w-3 rotate-90 opacity-50" />
                                                </div>
                                              </div>
                                            </th>
                                            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-r border-gray-200">
                                              <div className="flex items-center justify-center gap-1">
                                                Add
                                                <div className="flex flex-col">
                                                  <ChevronRight className="h-3 w-3 rotate-[-90deg] opacity-50" />
                                                  <ChevronRight className="h-3 w-3 rotate-90 opacity-50" />
                                                </div>
                                              </div>
                                            </th>
                                            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-r border-gray-200">
                                              <div className="flex items-center justify-center gap-1">
                                                Edit
                                                <div className="flex flex-col">
                                                  <ChevronRight className="h-3 w-3 rotate-[-90deg] opacity-50" />
                                                  <ChevronRight className="h-3 w-3 rotate-90 opacity-50" />
                                                </div>
                                              </div>
                                            </th>
                                            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-r border-gray-200">
                                              <div className="flex items-center justify-center gap-1">
                                                Delete
                                                <div className="flex flex-col">
                                                  <ChevronRight className="h-3 w-3 rotate-[-90deg] opacity-50" />
                                                  <ChevronRight className="h-3 w-3 rotate-90 opacity-50" />
                                                </div>
                                              </div>
                                            </th>
                                            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                              <div className="flex items-center justify-center gap-1">
                                                View
                                                <div className="flex flex-col">
                                                  <ChevronRight className="h-3 w-3 rotate-[-90deg] opacity-50" />
                                                  <ChevronRight className="h-3 w-3 rotate-90 opacity-50" />
                                                </div>
                                              </div>
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {paginatedScreens.map((screen, index) => {
                                            // Screen from API response has: screen_id, screen_name, screen_label, can_view, can_insert, can_update, can_delete
                                            const screenId = screen.screen_id;
                                            
                                            // Get permissions from state (which was populated from API), or use API response directly
                                            const permissions = screenPermissions[screenId] || {
                                              canView: Boolean(screen.can_view),
                                              canInsert: Boolean(screen.can_insert),
                                              canUpdate: Boolean(screen.can_update),
                                              canDelete: Boolean(screen.can_delete)
                                            };
                                            
                                            const rowNumber = startIndex + index + 1;
                                            
                                            return (
                                              <tr key={screenId || index} className="border-b border-gray-200 hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200">
                                                  {rowNumber}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200">
                                                  {screen.screen_label || screen.screen_name || 'Unknown Screen'}
                                                </td>
                                                <td className="px-4 py-3 text-center border-r border-gray-200">
                                                  <input
                                                    type="checkbox"
                                                    checked={permissions.canInsert}
                                                    onChange={() => toggleScreenPermission(screenId, 'canInsert')}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                                  />
                                                </td>
                                                <td className="px-4 py-3 text-center border-r border-gray-200">
                                                  <input
                                                    type="checkbox"
                                                    checked={permissions.canUpdate}
                                                    onChange={() => toggleScreenPermission(screenId, 'canUpdate')}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                                  />
                                                </td>
                                                <td className="px-4 py-3 text-center border-r border-gray-200">
                                                  <input
                                                    type="checkbox"
                                                    checked={permissions.canDelete}
                                                    onChange={() => toggleScreenPermission(screenId, 'canDelete')}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                                  />
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                  <input
                                                    type="checkbox"
                                                    checked={permissions.canView}
                                                    onChange={() => toggleScreenPermission(screenId, 'canView')}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                                  />
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                    
                                    {/* Pagination */}
                                    <div className="flex items-center justify-between mt-4">
                                      <div className="text-sm text-gray-700">
                                        Showing {totalCount > 0 ? startIndex + 1 : 0} to {endIndex} of {totalCount} entries
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setPermissionCurrentPage(prev => Math.max(0, prev - 1))}
                                          disabled={permissionCurrentPage === 0}
                                          className="border-gray-300 disabled:opacity-50"
                                        >
                                          Previous
                                        </Button>
                                        <div className="flex items-center gap-1">
                                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                            <button
                                              key={pageNum}
                                              onClick={() => setPermissionCurrentPage(pageNum - 1)}
                                              className={`px-3 py-1 text-sm rounded ${
                                                permissionCurrentPage === pageNum - 1
                                                  ? 'bg-blue-600 text-white'
                                                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                              }`}
                                            >
                                              {pageNum}
                                            </button>
                                          ))}
                                        </div>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setPermissionCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                                          disabled={permissionCurrentPage >= totalPages - 1}
                                          className="border-gray-300 disabled:opacity-50"
                                        >
                                          Next
                                        </Button>
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
            <div className="border-t border-gray-200 p-4 flex items-center justify-end gap-3 flex-shrink-0">
              <Button
                variant="outline"
                onClick={handleClosePermissionModal}
                disabled={savingPermissions}
                className="border-[#006600] text-[#006600] hover:bg-green-50 disabled:border-gray-300 disabled:text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePermissions}
                disabled={savingPermissions || Object.keys(screenPermissions).length === 0}
                className="bg-[#006600] hover:bg-[#005500] text-white border-[#006600] disabled:bg-gray-400 disabled:border-gray-400"
              >
                {savingPermissions ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Permissions
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showMasterFeatureModal && selectedRoleForMasterFeatures && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col rounded-xl shadow-lg border border-gray-200 bg-white">
            <CardHeader className="border-b border-gray-200 flex-shrink-0 pb-4">
              <div className="flex items-start justify-between gap-4">
                <CardTitle className="text-xl font-bold text-gray-900 tracking-tight">
                  Master Features
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseMasterFeatureModal}
                  className="h-8 w-8 p-0 shrink-0 text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto pt-0 px-6">
              <div className="mb-4 pb-3 border-b border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-gray-900">
                    {selectedRoleForMasterFeatures.name || 'Role'}
                  </span>{' '}
                  (ID: {selectedRoleForMasterFeatures.id})
                </p>
              </div>

              {loadingMasterFeatures ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-500 mb-3" />
                  <p className="text-gray-600 text-sm">Loading features…</p>
                </div>
              ) : masterFeaturesCatalog.length === 0 ? (
                <div className="text-center py-12 text-gray-600 text-sm">
                  No master features found. Configure them under Organizational Management → Master Features.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600">Search</span>
                    <Input
                      type="text"
                      placeholder="Filter by name"
                      value={masterFeatureSearchQuery}
                      onChange={(e) => setMasterFeatureSearchQuery(e.target.value)}
                      className="max-w-xs h-9 text-sm border-gray-200"
                    />
                  </div>
                  <div className="space-y-3 pb-2">
                    {masterFeaturesCatalog
                      .filter((f) => {
                        const name = (f.feature_name || f.name || '').toLowerCase();
                        const q = masterFeatureSearchQuery.trim().toLowerCase();
                        return !q || name.includes(q);
                      })
                      .map((f) => {
                        const fid = f.id ?? f.feature_id;
                        const key = String(fid);
                        const perms = roleFeaturePermissions[key];
                        const enabled = Boolean(perms?.canView);
                        const canUpdate = Boolean(perms?.canUpdate);
                        const label = f.feature_name || f.name || f.featureName || `Feature ${fid}`;
                        return (
                          <div
                            key={key}
                            className="p-4 border border-gray-200 rounded-lg bg-white hover:bg-gray-50/80 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                              <div className="flex items-center gap-3 min-w-0">
                                <h4 className="text-base font-semibold text-gray-900 truncate">
                                  {label}
                                </h4>
                                <Badge
                                  variant="outline"
                                  className={
                                    enabled
                                      ? 'shrink-0 bg-green-50 text-green-700 border-green-200'
                                      : 'shrink-0 bg-gray-100 text-gray-600 border-gray-200'
                                  }
                                >
                                  {enabled ? 'Active' : 'Inactive'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 shrink-0 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">View</span>
                                  <Switch
                                    checked={enabled}
                                    disabled={savingMasterFeatures}
                                    onCheckedChange={(next) => toggleRoleMasterFeature(fid, next)}
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">Update</span>
                                  <Switch
                                    checked={canUpdate}
                                    disabled={savingMasterFeatures}
                                    onCheckedChange={(next) => toggleRoleMasterFeatureUpdate(fid, next)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 flex-shrink-0 bg-gray-50/50">
              <Button
                variant="outline"
                onClick={handleCloseMasterFeatureModal}
                disabled={savingMasterFeatures}
                className="border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMasterFeatures}
                disabled={savingMasterFeatures || loadingMasterFeatures || masterFeaturesCatalog.length === 0}
                className="bg-gray-900 hover:bg-gray-800 text-white border-gray-900 disabled:bg-gray-400"
              >
                {savingMasterFeatures ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;